/**
 * Header Scroll Effect
 * Clean implementation for logo color changes based on scroll position
 */

export const initHeaderScroll = () => {
  // Get DOM elements
  const header = document.querySelector('#header');
  const logoSymbolContainer = document.querySelector('header > div:first-child');
  const bookingForm = document.querySelector('#bookingForm');
  const mobileBookingToggle = document.querySelector('#mobileBookingToggle');
  const footer = document.querySelector('footer');
  const heroSection = document.querySelector('.home-carousel');


  if (!header) return;

  // State tracking
  let lastScrollY = window.scrollY;
  let isScrollingUp = false;

  /**
   * Check if we're over the hero section
   */
  const isOverHero = () => {
    if (!heroSection) return false;
    return heroSection.getBoundingClientRect().bottom > 0;
  };

  /**
   * Setup SVG gradients once
   */
  const setupGradients = () => {
    const svg = document.querySelector('header svg');
    if (!svg) return;

    let defs = svg.querySelector('defs');
    if (!defs) {
      defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
      svg.prepend(defs);
    }

    // Orange gradient - vertical (bottom to top)
    if (!document.getElementById('orange-gradient')) {
      const orangeGradient = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
      orangeGradient.id = 'orange-gradient';
      orangeGradient.setAttribute('x1', '0%');
      orangeGradient.setAttribute('y1', '100%');
      orangeGradient.setAttribute('x2', '0%');
      orangeGradient.setAttribute('y2', '0%');

      const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
      stop1.setAttribute('offset', '0%');
      stop1.setAttribute('style', 'stop-color:var(--color-orange-10)');

      const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
      stop2.setAttribute('offset', '100%');
      stop2.setAttribute('style', 'stop-color:var(--color-orange-90)');

      orangeGradient.appendChild(stop1);
      orangeGradient.appendChild(stop2);

      defs.appendChild(orangeGradient);
    }

    // Green gradient - vertical (bottom to top)
    if (!document.getElementById('green-gradient')) {
      const greenGradient = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
      greenGradient.id = 'green-gradient';
      greenGradient.setAttribute('x1', '0%');
      greenGradient.setAttribute('y1', '100%');
      greenGradient.setAttribute('x2', '0%');
      greenGradient.setAttribute('y2', '0%');

      const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
      stop1.setAttribute('offset', '0%');
      stop1.setAttribute('style', 'stop-color:var(--color-green-10)');

      const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
      stop2.setAttribute('offset', '100%');
      stop2.setAttribute('style', 'stop-color:var(--color-green-90)');

      greenGradient.appendChild(stop1);
      greenGradient.appendChild(stop2);

      defs.appendChild(greenGradient);
    }
  };

  /**
   * Animate SVG gradients with moving effect
   */
  const animateGradients = () => {
    const orangeGradient = document.getElementById('orange-gradient');
    const greenGradient = document.getElementById('green-gradient');

    if (!orangeGradient || !greenGradient) return;

    let time = 0;

    const animate = () => {
      time += 0.01;

      // Calculate animated positions (sine wave for smooth movement)
      const offsetOrange = Math.sin(time) * 20; // ±20% movement
      const offsetGreen = Math.sin(time + 1) * 20; // Offset by 1 radian for variation

      // Update orange gradient positions
      const orangeY1 = 100 + offsetOrange; // Start from bottom + offset
      const orangeY2 = 0 + offsetOrange; // End at top + offset
      orangeGradient.setAttribute('y1', `${orangeY1}%`);
      orangeGradient.setAttribute('y2', `${orangeY2}%`);

      // Update green gradient positions (slightly different movement)
      const greenY1 = 100 + offsetGreen;
      const greenY2 = 0 + offsetGreen;
      greenGradient.setAttribute('y1', `${greenY1}%`);
      greenGradient.setAttribute('y2', `${greenY2}%`);

      requestAnimationFrame(animate);
    };

    animate();
  };

  /**
   * Setup logo paths with IDs and transitions
   */
  const setupLogoPaths = () => {
    const svg = document.querySelector('header svg');
    if (!svg) return null;

    const paths = svg.querySelectorAll('path');
    if (paths.length < 2) return null;

    const firstPath = paths[0];
    const secondPath = paths[1];

    // Add IDs and transitions to paths
    firstPath.id = 'logo-path-1';
    secondPath.id = 'logo-path-2';

    // Add smooth transitions to all paths  
    firstPath.style.transition = 'fill 0.5s';
    secondPath.style.transition = 'fill 0.5s';

    // Ensure second path starts without fill attribute to let classes control it
    secondPath.removeAttribute('fill');

    // Second path styling is now handled by CSS - no setup needed!

    return { firstPath, secondPath };
  };

  /**
   * Setup layered gradient system for first path
   */
  const setupGradientLayers = () => {
    const svg = document.querySelector('header svg');
    if (!svg) return null;

    const firstPath = document.getElementById('logo-path-1');
    if (!firstPath) return null;

    // Create white layer
    let whiteLayer = document.getElementById('white-layer');
    if (!whiteLayer) {
      whiteLayer = firstPath.cloneNode(true);
      whiteLayer.id = 'white-layer';
      whiteLayer.setAttribute('fill', 'white');
      whiteLayer.style.fill = 'white';
      whiteLayer.style.transition = 'opacity 0.3s ease';
      whiteLayer.style.opacity = '1';
      whiteLayer.style.visibility = 'visible';
      firstPath.parentNode.insertBefore(whiteLayer, firstPath.nextSibling);
    }

    // Create orange layer
    let orangeLayer = document.getElementById('orange-layer');
    if (!orangeLayer) {
      orangeLayer = firstPath.cloneNode(true);
      orangeLayer.id = 'orange-layer';
      orangeLayer.setAttribute('fill', 'url(#orange-gradient)');
      orangeLayer.style.fill = 'url(#orange-gradient)';
      orangeLayer.style.transition = 'opacity 0.3s ease';
      orangeLayer.style.opacity = '0';
      orangeLayer.style.visibility = 'visible';
      whiteLayer.parentNode.insertBefore(orangeLayer, whiteLayer.nextSibling);
    }

    // Create green layer
    let greenLayer = document.getElementById('green-layer');
    if (!greenLayer) {
      greenLayer = firstPath.cloneNode(true);
      greenLayer.id = 'green-layer';
      greenLayer.setAttribute('fill', 'url(#green-gradient)');
      greenLayer.style.fill = 'url(#green-gradient)';
      greenLayer.style.transition = 'opacity 0.3s ease';
      greenLayer.style.opacity = '0';
      greenLayer.style.visibility = 'visible';
      orangeLayer.parentNode.insertBefore(greenLayer, orangeLayer.nextSibling);
    }

    // Make base path transparent
    firstPath.setAttribute('fill', 'transparent');
    firstPath.style.fill = 'transparent';

    return { whiteLayer, orangeLayer, greenLayer };
  };

  /**
   * Update logo path colors with smooth transitions
   */
  const updateLogoPaths = () => {
    const paths = setupLogoPaths();
    if (!paths) return;

    const { firstPath, secondPath } = paths;
    const overHero = isOverHero();

    // Second path is now handled by CSS:
    // .header-white a svg path:last-of-type { @apply fill-white; }
    // No JavaScript needed for second path anymore!

    // Handle first path with layered system
    const layers = setupGradientLayers();
    if (!layers) {
      // Fallback: direct gradients on first path
      if (overHero) {
        firstPath.setAttribute('fill', 'white');
        firstPath.style.fill = 'white';
      } else {
        if (isScrollingUp) {
          firstPath.setAttribute('fill', 'url(#green-gradient)');
          firstPath.style.fill = 'url(#green-gradient)';
        } else {
          firstPath.setAttribute('fill', 'url(#orange-gradient)');
          firstPath.style.fill = 'url(#orange-gradient)';
        }
      }
      return;
    }

    const { whiteLayer, orangeLayer, greenLayer } = layers;

    // Make base path transparent
    firstPath.setAttribute('fill', 'transparent');
    firstPath.style.fill = 'transparent';

    if (overHero) {
      // Over hero: show white layer only
      whiteLayer.style.opacity = '1';
      whiteLayer.style.visibility = 'visible';
      whiteLayer.setAttribute('fill', 'white');
      whiteLayer.style.fill = 'white';
      orangeLayer.style.opacity = '0';
      greenLayer.style.opacity = '0';
    } else {
      // Outside hero: hide white, show appropriate gradient
      whiteLayer.style.opacity = '0';

      if (isScrollingUp) {
        orangeLayer.style.opacity = '0';
        greenLayer.style.opacity = '1';
        greenLayer.style.visibility = 'visible';
      } else {
        orangeLayer.style.opacity = '1';
        orangeLayer.style.visibility = 'visible';
        greenLayer.style.opacity = '0';
      }
    }
  };

  /**
   * Update header classes
   */
  const updateHeaderClasses = () => {
    const overHero = isOverHero();

    if (overHero) {
      header.classList.add('header-white');
      header.classList.remove('header-black');

      if (logoSymbolContainer) {
        logoSymbolContainer.classList.add('header-white');
        logoSymbolContainer.classList.remove('header-black');
      }
    } else {
      header.classList.remove('header-white');
      header.classList.add('header-black');

      if (logoSymbolContainer) {
        logoSymbolContainer.classList.remove('header-white');
        logoSymbolContainer.classList.add('header-black');
      }

      // Update navigation items
      const navItems = document.querySelectorAll('header a, header button, header div, header span');
      navItems.forEach(item => {
        item.classList.remove('color-white');
        item.classList.add('text-primary');
      });
    }
  };

  /**
   * Booking form functions
   */
  const shouldShowBookingForm = () => {
    if (!heroSection) return true;
    return window.scrollY > 50;
  };

  const isBookingFormIntersectingFooter = () => {
    if (!footer || !bookingForm) return false;
    const footerRect = footer.getBoundingClientRect();
    const bookingFormRect = bookingForm.getBoundingClientRect();
    return bookingFormRect.bottom >= footerRect.top;
  };

  const isBookingFormIntersectingHero = () => {
    if (!heroSection || !bookingForm) return false;
    const heroRect = heroSection.getBoundingClientRect();
    const bookingFormRect = bookingForm.getBoundingClientRect();
    return bookingFormRect.top <= heroRect.bottom;
  };

  const isMobileToggleIntersectingHero = () => {
    if (!heroSection || !mobileBookingToggle) return false;
    const heroRect = heroSection.getBoundingClientRect();
    const toggleRect = mobileBookingToggle.getBoundingClientRect();
    return toggleRect.top <= heroRect.bottom;
  };

  const isMobileToggleIntersectingFooter = () => {
    if (!footer || !mobileBookingToggle) return false;
    const footerRect = footer.getBoundingClientRect();
    const toggleRect = mobileBookingToggle.getBoundingClientRect();
    return toggleRect.bottom >= footerRect.top;
  };

  const updateBookingForm = () => {
    if (!bookingForm) return;
    
    const shouldHide = isBookingFormIntersectingHero() || isBookingFormIntersectingFooter();
    const shouldShow = shouldShowBookingForm() && !shouldHide;
    
    if (shouldHide) {
      bookingForm.classList.remove('opacity-100', 'translate-y-0');
      bookingForm.classList.add('opacity-0', 'invisible', 'translate-y-4');
    } else if (shouldShow) {
      bookingForm.classList.remove('opacity-0', 'invisible', 'translate-y-4');
      bookingForm.classList.add('opacity-100', 'translate-y-0');
    } else {
      bookingForm.classList.remove('opacity-100', 'translate-y-0');
      bookingForm.classList.add('opacity-0', 'invisible', 'translate-y-4');
    }
    
    // Update mobile booking toggle button with its own intersection checks
    if (mobileBookingToggle) {
      const mobileToggleShouldHide = isMobileToggleIntersectingHero() || isMobileToggleIntersectingFooter();
      const mobileToggleShouldShow = shouldShowBookingForm() && !mobileToggleShouldHide;
      
      if (mobileToggleShouldHide) {
        mobileBookingToggle.classList.remove('opacity-100', 'translate-y-0');
        mobileBookingToggle.classList.add('opacity-0', 'invisible', 'translate-y-4');
      } else if (mobileToggleShouldShow) {
        mobileBookingToggle.classList.remove('opacity-0', 'invisible', 'translate-y-4');
        mobileBookingToggle.classList.add('opacity-100', 'translate-y-0');
      } else {
        mobileBookingToggle.classList.remove('opacity-100', 'translate-y-0');
        mobileBookingToggle.classList.add('opacity-0', 'invisible', 'translate-y-4');
      }
    }
  };

  /**
   * Handle scroll position and direction
   */
  const handleScrollDirection = () => {
    const currentScrollY = window.scrollY;
    const scrollingDown = currentScrollY > lastScrollY;

    // Update scroll direction
    if (currentScrollY !== lastScrollY) {
      // Special case: when just leaving hero, default to scrolling down
      const overHero = isOverHero();
      if (!overHero && lastScrollY < 10) {
        isScrollingUp = false;
      } else {
        isScrollingUp = !scrollingDown;
      }
    }

    // Show/hide header based on scroll position
    if (currentScrollY < 100 || !scrollingDown) {
      header.classList.remove('-translate-y-32');
      header.classList.add('translate-y-0');
    } else if (scrollingDown && currentScrollY > 100) {
      header.classList.remove('translate-y-0');
      header.classList.add('-translate-y-32');
    }

    // Hide header when footer is visible
    if (footer) {
      const footerTop = footer.getBoundingClientRect().top;
      if (footerTop <= window.innerHeight) {
        header.classList.remove('translate-y-0');
        header.classList.add('-translate-y-32');
      }
    }

    lastScrollY = currentScrollY;
  };

  /**
   * Main scroll handler
   */
  const handleScroll = () => {
    handleScrollDirection();
    updateHeaderClasses();
    updateLogoPaths();
    updateBookingForm();
  };

  /**
   * Initialize everything
   */
  const init = () => {
    setupGradients();
    setupLogoPaths();
    isScrollingUp = false;

    // Initial booking form state
    if (bookingForm) {
      bookingForm.classList.add('opacity-0', 'invisible', 'translate-y-4');
    }

    // Initial mobile booking toggle state
    if (mobileBookingToggle) {
      mobileBookingToggle.classList.add('opacity-0', 'invisible', 'translate-y-4');
    }

    // Set initial logo colors
    updateLogoPaths();

    // Start gradient animations
    animateGradients();

    // Add event listener
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Initial update
    handleScroll();
  };

  // Start
  init();

  // Public API
  return {
    refresh: handleScroll,
    hide: () => {
      header.classList.remove('translate-y-0');
      header.classList.add('-translate-y-32');
    },
    show: () => {
      if (!footer || footer.getBoundingClientRect().top > window.innerHeight) {
        header.classList.add('translate-y-0');
        header.classList.remove('-translate-y-32');
      }
    }
  };
};